import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ArrowLeft, Building2, Edit, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { GET_ORGANIZATION, type Organization } from '@/graphql/organizations';
import {
  OverviewTab,
  ContactsTab,
  ActivitiesTab,
  FilesTab,
  PricingTab,
  InvoicesStatementsTab,
  BlastTab,
} from './components';

type TabId = 'overview' | 'contacts' | 'activities' | 'files' | 'pricing' | 'invoices' | 'blast';

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'activities', label: 'Activities' },
  { id: 'files', label: 'Files' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'invoices', label: 'Invoices & Statements' },
  { id: 'blast', label: 'BLAST' },
];

export function ClientDetail() {
  const { clientId } = useParams();
  const { canWrite } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Fetch organization data from GraphQL
  const { data, loading, error, refetch } = useQuery(GET_ORGANIZATION, {
    variables: { id: clientId },
    skip: !clientId,
  });

  const organization = data?.organization as Organization | null;

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-pond" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card p-8 text-center">
        <p className="text-red-600 dark:text-red-400">
          Error loading organization: {error.message}
        </p>
        <Link to="/clients" className="btn-secondary mt-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Clients
        </Link>
      </div>
    );
  }

  // Not found state
  if (!organization) {
    return (
      <div className="card p-8 text-center">
        <p className="text-slate dark:text-mist">Organization not found</p>
        <Link to="/clients" className="btn-secondary mt-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Clients
        </Link>
      </div>
    );
  }

  // Map organization data to client format for tab components
  const client = {
    id: organization.id,
    name: organization.name,
    type: organization.type.toLowerCase() as 'cfi' | 'psp',
    status: organization.status.toLowerCase() as 'prospect' | 'active' | 'inactive',
    contactName: organization.primaryContact
      ? `${organization.primaryContact.firstName} ${organization.primaryContact.lastName}`
      : undefined,
    contactEmail: organization.primaryContact?.email,
    contactPhone: organization.primaryContact?.phone || undefined,
    website: organization.website || undefined,
    address: organization.addresses?.[0]
      ? `${organization.addresses[0].street1}, ${organization.addresses[0].city}, ${organization.addresses[0].state} ${organization.addresses[0].zipCode}`
      : undefined,
    createdAt: new Date(organization.createdAt),
    blastTier: organization.blastTier,
    notes: '',
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab client={client} />;
      case 'contacts':
        return (
          <ContactsTab 
            clientId={client.id} 
            contacts={organization.contacts}
            onRefresh={() => refetch()}
          />
        );
      case 'activities':
        return (
          <ActivitiesTab 
            clientId={client.id}
            clientName={client.name} 
            contactEmail={client.contactEmail || ''} 
          />
        );
      case 'files':
        return <FilesTab clientId={client.id} />;
      case 'pricing':
        return <PricingTab clientId={client.id} />;
      case 'invoices':
        return <InvoicesStatementsTab clientId={client.id} />;
      case 'blast':
        return (
          <BlastTab
            clientId={client.id}
            clientType={organization.type}
            blastTier={organization.blastTier}
            blastFeatures={organization.blastFeatures}
            onTierUpdate={() => refetch()}
          />
        );
      default:
        return <OverviewTab client={client} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb & Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/clients"
          className="btn-ghost btn-icon"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-pond dark:text-pond-light" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate dark:text-mist">
                {organization.name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="badge-primary">{organization.type}</span>
                <span className={cn(
                  'badge',
                  organization.status === 'ACTIVE' ? 'badge-secondary' : 'badge-neutral'
                )}>
                  {organization.status.charAt(0) + organization.status.slice(1).toLowerCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
        {canWrite('clients') && (
          <button className="btn-secondary">
            <Edit className="w-4 h-4" />
            Edit Client
          </button>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn('tab', activeTab === tab.id && 'active')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="animate-fade-in">
        {renderTabContent()}
      </div>
    </div>
  );
}
